import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "input", "form", "customFields", "vatLines", "statusContainer", "salesStatus", "purchasesStatus" ]

  connect() {
    this.updateStyles()
    this.setupDynamicValidation()
  }

  setupDynamicValidation() {
    if (!this.hasStatusContainerTarget) return
    // Enable validation if any status indicator is shown
    const showSales = this.statusContainerTarget.dataset.showSalesStatus === "true"
    const showPurchases = this.statusContainerTarget.dataset.showPurchasesStatus === "true"
    if (!showSales && !showPurchases) return

    // Cache required fields from data attributes (avoid JSON.parse on every keystroke)
    this.requiredInvoiceFields = JSON.parse(this.statusContainerTarget.dataset.requiredInvoiceFields || "[]")
    this.requiredVatFields = JSON.parse(this.statusContainerTarget.dataset.requiredVatFields || "[]")

    // Listen for input and change events on custom fields
    this.element.addEventListener("input", this.handleFieldInput.bind(this))
    this.element.addEventListener("change", this.handleFieldInput.bind(this))
  }

  handleFieldInput(event) {
    const input = event.target
    // Only care about custom field inputs and VAT classification
    const isCustomField = input.name?.includes("[custom_fields]") || input.closest?.(".custom-field")
    const isVatClassification = input.name?.includes("[vat_classification_id]") || input.closest?.("[data-controller*='form-vat-selector']")

    if (!isCustomField && !isVatClassification) return

    this.updateStatusIndicators()
  }

  updateStatusIndicators() {
    if (!this.hasStatusContainerTarget) return
    const showSales = this.statusContainerTarget.dataset.showSalesStatus === "true"
    const showPurchases = this.statusContainerTarget.dataset.showPurchasesStatus === "true"
    if (!showSales && !showPurchases) return

    const currentRole = this.determineRole(this.inputTarget.value)

    // Only update status for current perspective if it's visible
    if (currentRole === "seller" && showSales) {
      const salesComplete = this.checkPerspectiveComplete("seller", this.requiredInvoiceFields, this.requiredVatFields)
      this.updateStatusDOM("sales", salesComplete)
    }
    if (currentRole === "buyer" && showPurchases) {
      const purchasesComplete = this.checkPerspectiveComplete("buyer", this.requiredInvoiceFields, this.requiredVatFields)
      this.updateStatusDOM("purchases", purchasesComplete)
    }
  }

  checkPerspectiveComplete(role, requiredInvoiceFields, requiredVatFields) {
    const currentRole = this.determineRole(this.inputTarget.value)
    const isCurrentPerspective = role === currentRole

    // Check invoice-level custom fields
    for (const fieldName of requiredInvoiceFields) {
      const hiddenInput = this.element.querySelector(`input[name="invoice[custom_fields][${role}][${fieldName}]"]`)
      const customField = hiddenInput?.closest('.custom-field')
      const textInput = customField?.querySelector('input[type="text"]')
      // For current perspective: use text input (real-time). For other: use hidden input only
      const value = isCurrentPerspective ? (textInput?.value || hiddenInput?.value || '') : (hiddenInput?.value || '')
      if (!value.trim()) {
        return false
      }
    }

    // Check VAT line custom fields
    if (requiredVatFields.length > 0) {
      // Only select actual VAT line elements, not containers or templates
      const vatLines = this.element.querySelectorAll(".vat-line")

      for (const vatLine of vatLines) {
        // Skip if this vat line is marked for destruction
        const destroyInput = vatLine.querySelector('input[name*="_destroy"]')
        if (destroyInput?.value === "1" || destroyInput?.value === "true") {
          continue
        }

        for (const fieldName of requiredVatFields) {
          // VAT classification is special - check the hidden input
          if (fieldName === "vat_classification") {
            const vatClassInput = vatLine.querySelector('input[name*="[vat_classification_id]"]')
            if (!vatClassInput || !vatClassInput.value.trim()) {
              return false
            }
          } else {
            const hiddenInput = vatLine.querySelector(`input[name*="[custom_fields][${role}][${fieldName}]"]`)
            const customField = hiddenInput?.closest('.custom-field')
            const textInput = customField?.querySelector('input[type="text"]')
            // For current perspective: use text input (real-time). For other: use hidden input only
            const value = isCurrentPerspective ? (textInput?.value || hiddenInput?.value || '') : (hiddenInput?.value || '')
            if (!value.trim()) {
              return false
            }
          }
        }
      }
    }

    return true
  }

  updateStatusDOM(perspective, isComplete) {
    const hasTarget = perspective === "sales" ? this.hasSalesStatusTarget : this.hasPurchasesStatusTarget
    if (!hasTarget) return
    const target = perspective === "sales" ? this.salesStatusTarget : this.purchasesStatusTarget

    if (isComplete) {
      // Hide indicator when complete - no news is good news
      target.classList.add("hidden")
      target.innerHTML = ""
    } else {
      target.classList.remove("hidden")
      target.innerHTML = `<span class="inline-flex items-center justify-center w-4 h-4">
        <span class="inline-block w-2 h-2 rounded-full bg-amber-400 ring-2 ring-amber-400/30 animate-pulse"></span>
      </span>`
    }
  }

  updateStyles() {
    const operationType = this.inputTarget.value || "sales"

    // Update form container class for CSS styling
    const formContainer = this.element.querySelector(".operation-sales, .operation-purchases, .operation-container")
    if (formContainer) {
      formContainer.classList.remove('operation-sales', 'operation-purchases')
      formContainer.classList.add(`operation-${operationType}`)
    }
  }

  determineRole(operationType) {
    return operationType === 'sales' ? 'seller' : 'buyer'
  }
}
